// Settings › Consent (UC-GOVERNANCE-016; BR-GOVERNANCE-019, -024; D43, D58): the six consent purposes in the order every form
// shows them, which optional ones the kiosk pre-ticks, each description in the practice's own words, and the privacy-contact
// email the notice names. No prototype screen exists for it; it follows the settings screens' Operate pattern. Owner only
// (`tenancy.privacy.write`): anyone else sees the no-access panel. A save sends only what changed; a description emptied or set
// back to the platform wording restores the platform wording; an emptied contact falls back to the Information Officer.
// What it configures is asked on the kiosk and in the desk capture on a patient's Consents tab (UC-GOVERNANCE-013 A3); the
// header links to the re-consent report (UC-GOVERNANCE-017 A4).
import {
  CONSENT_DESCRIPTION_MAX_LENGTH, ConsentSettingsSchema, consentPurposeDefinition, renderConsentPlaceholders,
  type ConsentPurpose, type ConsentSettings, type ConsentSettingsPurpose, type ConsentSettingsView,
} from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { consent } from "../api/endpoints.consent";
import { isApiError } from "../api/problem";
import { formatDateTime } from "../lib/format";
import { useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, Chip, ErrorAlert, Field, Loading, Panel, TextArea, TextInput } from "../ui/bits";
import { NoAccessPanel, PageHead, SettingsTabs } from "./common";

interface Draft { preTicked: Record<ConsentPurpose, boolean>; template: Record<ConsentPurpose, string>; email: string }
type Errors = Record<string, string>;
const FORM_ID = "consent-settings";

const draftOf = (v: ConsentSettingsView): Draft => ({
  preTicked: Object.fromEntries(v.purposes.map((p) => [p.purpose, p.preTicked])) as Record<ConsentPurpose, boolean>,
  template: Object.fromEntries(v.purposes.map((p) => [p.purpose, p.descriptionTemplate])) as Record<ConsentPurpose, string>,
  email: v.privacyContactEmail.source === "setting" ? (v.privacyContactEmail.value ?? "") : "",
});

/** An emptied description means the platform wording. */
const effectiveTemplate = (typed: string, purpose: ConsentPurpose): string => typed.trim() || consentPurposeDefinition(purpose).description;

/** The PUT body: only the purposes and fields that changed; `description: ""` restores the platform wording (UC-GOVERNANCE-016 A1). */
export function settingsBody(view: ConsentSettingsView, d: Draft): ConsentSettings {
  const purposes: NonNullable<ConsentSettings["purposes"]> = [];
  for (const p of view.purposes) {
    const entry: NonNullable<ConsentSettings["purposes"]>[number] = { purpose: p.purpose };
    if (p.configurable && d.preTicked[p.purpose] !== p.preTicked) entry.preTicked = d.preTicked[p.purpose];
    const next = effectiveTemplate(d.template[p.purpose], p.purpose);
    if (next !== p.descriptionTemplate.trim()) entry.description = next === consentPurposeDefinition(p.purpose).description ? "" : next;
    if (Object.keys(entry).length > 1) purposes.push(entry);
  }
  const body: ConsentSettings = {};
  if (purposes.length > 0) body.purposes = purposes;
  const email = d.email.trim().toLowerCase();
  const stored = view.privacyContactEmail.source === "setting" ? (view.privacyContactEmail.value ?? "") : "";
  if (email !== stored) body.privacyContactEmail = email === "" ? null : email;
  return body;
}

/** A field path of the body (`purposes.1.description`) → the screen's key (`description.MedicalAidSharing`). */
function keyOf(path: string, body: ConsentSettings): string {
  const m = /^purposes\.(\d+)\.(\w+)/.exec(path);
  if (m) {
    const purpose = body.purposes?.[Number(m[1])]?.purpose;
    if (purpose) return `${m[2]}.${purpose}`;
  }
  return path.startsWith("privacyContactEmail") ? "email" : path;
}

/** The device's own wording for the two fields a person can get wrong; anything else keeps the schema's message. */
const messageFor = (key: string, message: string): string =>
  key === "email" ? "This is not an email address." : key.startsWith("description.") ? `At most ${CONSENT_DESCRIPTION_MAX_LENGTH} characters.` : message;

export function SettingsConsent() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const view = useQuery({ queryKey: ["consent-settings"], queryFn: () => consent.settings() });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const v = view.data?.data ?? null;

  useEffect(() => { if (v) setDraft(draftOf(v)); }, [v]);

  const body = v && draft ? settingsBody(v, draft) : null;
  const dirty = body !== null && Object.keys(body).length > 0;
  const practiceName = s?.organisation.tradingName ?? s?.organisation.legalName ?? "";
  const email = draft?.email.trim() ?? "";
  const fallback = v?.privacyContactEmail.source === "informationOfficer" ? v.privacyContactEmail.value : null;
  const contact = email || fallback;

  const save = async () => {
    if (!v || !draft || !body) return;
    setNotice(null);
    const e: Errors = {};
    const parsed = ConsentSettingsSchema.safeParse(body);
    if (!parsed.success) for (const i of parsed.error.issues) { const k = keyOf(i.path.map(String).join("."), body); e[k] = e[k] ?? messageFor(k, i.message); }
    setErrors(e);
    if (Object.keys(e).length > 0) {
      // The first refused field in screen order: the purposes top to bottom, then the contact.
      const first = [...v.purposes.map((p) => `description.${p.purpose}`), "email"].find((k) => e[k]) ?? Object.keys(e)[0]!;
      setTimeout(() => document.getElementById(`cs-${first.replace(".", "-")}`)?.focus(), 0);
      return;
    }
    setBusy(true);
    try {
      const { data } = await consent.putSettings(parsed.success ? parsed.data : body);
      qc.setQueryData<Awaited<ReturnType<typeof consent.settings>>>(["consent-settings"], (old) => (old ? { ...old, data } : old));
      setDraft(draftOf(data));
      setNotice(<Alert tone="ok"><b>Consent settings saved.</b> The next consent form shows them; consents already signed are unchanged.</Alert>);
    } catch (err) {
      if (isApiError(err) && err.code === "VALIDATION_FAILED" && err.errors.length > 0) {
        const se: Errors = {};
        for (const f of err.errors) { const k = keyOf(f.path, body); se[k] = f.message; }
        setErrors(se);
      }
      setNotice(<ErrorAlert error={err} />);
    } finally {
      setBusy(false);
    }
  };

  const set = (patch: (d: Draft) => Draft) => { setDraft((d) => (d ? patch(d) : d)); setNotice(null); };

  return (
    <section className="screen" id="s-settings-consent">
      <div className="content">
        <PageHead title="Consent" sub="What the kiosk and the desk capture on a patient's Consents tab ask, in this order on every form (POPIA; D43)" actions={<>
          <Link className="btn" to="/patients/re-consent" title="The patients whose consent is missing or on replaced wording">Re-consent report</Link>
          {v && <button className="btn primary" type="submit" form={FORM_ID} disabled={!online || busy || !dirty} title={block ? WRITE_BLOCK_TITLE[block] : undefined}>{busy ? "Saving…" : "Save changes"}</button>}
        </>} />
        <SettingsTabs />
        {notice}
        {view.isPending && <Loading what="consent settings" />}
        {view.isError && (isApiError(view.error) && view.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="Consent settings" error={view.error} /> : <ErrorAlert error={view.error} />)}
        {v && draft && (
          <form id={FORM_ID} noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
            <div className="split wide">
              <Panel title="Consent purposes" meta="six, always in this order" tight foot={<span className="help">{"{practice}"} is replaced by {practiceName || "the practice's name"} and {"{privacyContactEmail}"} by the privacy contact when the form is shown; the saved wording keeps the placeholders.</span>}>
                <div className="list">
                  {v.purposes.map((p) => (
                    <PurposeRow key={p.purpose} p={p} draft={draft} errors={errors} practiceName={practiceName} contact={contact}
                      onTick={(on) => set((d) => ({ ...d, preTicked: { ...d.preTicked, [p.purpose]: on } }))}
                      onText={(text) => set((d) => ({ ...d, template: { ...d.template, [p.purpose]: text } }))} />
                  ))}
                </div>
              </Panel>
              <Panel title="Privacy contact" meta="named in the consent text and the notice" foot={v.updatedAt ? <span className="help">Last saved {formatDateTime(v.updatedAt)}.</span> : <span className="help">Not changed since the platform defaults.</span>}>
                <div className="stack">
                  <Field label="Privacy-contact email" htmlFor="cs-email" error={errors.email}
                    help={email ? "Patients write here to withdraw consent or ask for their information." : fallback ? <>Empty: the Information Officer's email, {fallback}, is used.</> : undefined}>
                    <TextInput id="cs-email" type="email" inputMode="email" autoComplete="email" maxLength={254} value={draft.email} error={!!errors.email} placeholder={fallback ?? "popia@practice.co.za"}
                      onChange={(e) => set((d) => ({ ...d, email: e.target.value }))} />
                  </Field>
                  {!contact && <Alert tone="warn"><b>No privacy contact.</b> The consent text's contact line is left blank until an address is entered here or an Information Officer is named under Settings › General.</Alert>}
                </div>
              </Panel>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function PurposeRow({ p, draft, errors, practiceName, contact, onTick, onText }: {
  p: ConsentSettingsPurpose; draft: Draft; errors: Errors; practiceName: string; contact: string | null;
  onTick: (on: boolean) => void; onText: (text: string) => void;
}) {
  const typed = draft.template[p.purpose];
  const platform = consentPurposeDefinition(p.purpose).description;
  const own = effectiveTemplate(typed, p.purpose) !== platform;
  const err = errors[`description.${p.purpose}`];
  const tickNote = p.required ? "Required: always ticked, and cannot be withdrawn." : p.configurable ? "The patient can untick it." : "A deliberate opt-in: never pre-ticked (D43).";
  return (
    <div className="item consent-purpose">
      <span className="t" aria-hidden="true">{p.order}</span>
      <div className="grow stack gap-8">
        <div className="row c gap-8 wrap">
          <span className="primary" id={`cs-label-${p.purpose}`}>{p.label}</span>
          {p.required && <Chip tone="accent">Required</Chip>}
          {!p.required && !p.configurable && <Chip tone="neutral">Opt-in only</Chip>}
          {p.perChannel && <Chip tone="neutral">Per channel</Chip>}
        </div>
        <div className="row c gap-8 wrap">
          <Checkbox name={`preTicked-${p.purpose}`} checked={draft.preTicked[p.purpose]} disabled={!p.configurable} onChange={onTick}>Ticked when the form opens</Checkbox>
          <span className="help">{tickNote}</span>
        </div>
        <Field label={<>Description <span className="faint">· {own ? "your wording" : "platform wording"}</span></>} htmlFor={`cs-description-${p.purpose}`} error={err}>
          <TextArea id={`cs-description-${p.purpose}`} aria-describedby={`cs-count-${p.purpose}`} rows={3} value={typed} placeholder={platform} onChange={(e) => onText(e.target.value)} />
        </Field>
        <div className="row c gap-8 wrap small">
          <span className={typed.trim().length > CONSENT_DESCRIPTION_MAX_LENGTH ? "over" : "faint"} id={`cs-count-${p.purpose}`}>{typed.trim().length} / {CONSENT_DESCRIPTION_MAX_LENGTH}</span>
          {own && <button type="button" className="linkbtn" onClick={() => onText(platform)}>Restore platform wording</button>}
        </div>
        <div className="small muted">Shown as: {renderConsentPlaceholders(effectiveTemplate(typed, p.purpose), practiceName, contact)}</div>
      </div>
    </div>
  );
}
